import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Landmark, CheckCircle, ArrowRight, Calendar, DollarSign, Clock, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';

const LendingSection = () => {
  const lendingBenefits = [
    "Competitive rates from vetted private lenders",
    "Fast approvals when time is critical",
    "Flexible terms tailored to your project",
    "Solutions when banks say no",
    "Bridge loans, fix & flip, and construction financing",
    "No prepayment penalties on most programs"
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const lendingPrograms = [
    {
      icon: DollarSign,
      title: 'Bridge Loans',
      description: 'Short-term financing to bridge the gap between purchase and permanent financing'
    },
    {
      icon: Clock,
      title: 'Fix & Flip Financing',
      description: 'Quick funding for acquisition and renovation of investment properties'
    },
    {
      icon: ShieldCheck,
      title: 'Construction Loans',
      description: 'Ground-up construction and major renovation project financing'
    }
  ];

  return (
    <section id="lending" className="relative gradient-section py-16 lg:py-24 overflow-hidden">
      {/* Animated Decorative Elements */}
      <motion.div 
        className="absolute top-20 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-[120px]"
        animate={{ 
          x: [0, 30, 0],
          y: [0, -20, 0],
          scale: [1, 1.1, 1]
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div 
        className="absolute bottom-20 left-0 w-80 h-80 bg-[#1a2940]/50 rounded-full blur-[100px]"
        animate={{ 
          x: [0, -20, 0],
          y: [0, 30, 0]
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />
      
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div 
              className="inline-flex items-center gap-2 px-3 py-1.5 glass border border-[#D4AF37]/30 rounded-full mb-5"
              variants={itemVariants}
              whileHover={{ scale: 1.05, borderColor: 'rgba(212, 175, 55, 0.6)' }}
            >
              <Landmark className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-label text-[#D4AF37]">Private Lending</span>
            </motion.div>
            
            <motion.h2 
              className="heading-h2 mb-5"
              variants={itemVariants}
            >
              When Conventional Lenders
              <span className="gradient-text"> Say No</span>
            </motion.h2>
            
            <motion.p 
              className="text-body mb-8"
              variants={itemVariants}
            >
              We provide access to our network of private lenders offering attractive rates and 
              flexible terms. Whether you need bridge financing, rehab loans, or construction funding — 
              we have solutions when traditional banks can't help.
            </motion.p>

            {/* Benefits List */}
            <motion.div 
              className="grid sm:grid-cols-2 gap-4 mb-10"
              variants={containerVariants}
            >
              {lendingBenefits.map((benefit, index) => (
                <motion.div 
                  key={index} 
                  className="flex items-start gap-3"
                  variants={itemVariants}
                  whileHover={{ x: 5 }}
                >
                  <motion.div
                    whileHover={{ scale: 1.2, rotate: 10 }}
                  >
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  </motion.div>
                  <span className="text-white/70 text-sm">{benefit}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA */}
            <motion.div variants={itemVariants}>
              <Link to="/contact">
                <button className="cta-contact px-8 py-4 h-14 rounded-full text-lg group">
                  <Calendar className="h-5 w-5" />
                  Discuss Your Financing Needs
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </button>
              </Link>
            </motion.div>
          </motion.div>

          {/* Right - Stats/Info Cards */}
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Main Card */}
            <motion.div 
              className="card-modern border border-[#D4AF37]/30 rounded-2xl p-8"
              whileHover={{ boxShadow: '0 0 40px rgba(212, 175, 55, 0.15)' }}
            >
              <h3 className="gradient-text text-lg font-semibold mb-6 uppercase tracking-wider">
                Lending Programs
              </h3>
              
              <div className="space-y-6">
                {lendingPrograms.map((program, index) => (
                  <motion.div 
                    key={index}
                    className={`flex items-start gap-4 ${index < lendingPrograms.length - 1 ? 'pb-6 border-b border-white/10' : ''}`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    whileHover={{ x: 5 }}
                  >
                    <motion.div 
                      className="w-12 h-12 bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/10 rounded-xl flex items-center justify-center flex-shrink-0"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <program.icon className="h-6 w-6 text-[#D4AF37]" />
                    </motion.div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">{program.title}</h4>
                      <p className="text-white/50 text-sm">{program.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: 'Fast', label: 'Approval Process' },
                { value: 'Flexible', label: 'Loan Terms' }
              ].map((stat, index) => (
                <motion.div 
                  key={index}
                  className="card-modern border border-white/10 rounded-2xl p-6 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  whileHover={{ scale: 1.03, borderColor: 'rgba(212, 175, 55, 0.3)' }}
                >
                  <motion.span 
                    className={`${index === 0 ? 'gradient-text' : 'text-white'} text-3xl font-bold block`}
                    whileHover={{ scale: 1.1 }}
                  >
                    {stat.value}
                  </motion.span>
                  <p className="text-white/50 text-sm mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LendingSection;
